import { getAtAtToken } from '@ecomm/shared-cookies'
import { safeFetchJson } from '@jvlk/fp-ts-fetch'
import * as E from 'fp-ts/lib/Either'
import * as O from 'fp-ts/lib/Option'
import * as T from 'fp-ts/lib/Task'
import * as TE from 'fp-ts/lib/TaskEither'
import { pipe } from 'fp-ts/lib/function'

const atatApiUrl = process.env['ATAT_API_URL'] || ''

export const MAX_ATAT_RETRY_COUNT = 3
export const atatUrl = `${atatApiUrl}/api/visitor/page-load`

export type AtAtRequestBody = {
  readonly qq?: string
  readonly query?: string
  readonly referer?: string
  readonly title?: string
  readonly uid?: number | string
  readonly vid?: number | string
}

type ErrorHandler = (e: Error) => void
type SuccessHandler = (response: Record<string, string>) => void

function responseToError(res: Error | Response) {
  return res instanceof Error
    ? E.left(res)
    : E.left(new Error(`Unknown at-at error: ${JSON.stringify(res)}`))
}

function createAtAtFormData(params: AtAtRequestBody) {
  const platformParam = 'platform=fcp'
  const updatedQuery = params.query
    ? `${params.query}&${platformParam}`
    : `?${platformParam}`

  const formData = new FormData()

  params.qq && formData.append('qq', params.qq)
  formData.append('query', updatedQuery)
  params.referer && formData.append('referer', params.referer)
  params.title && formData.append('title', params.title)
  params.uid && formData.append('uid', String(params.uid))
  params.vid && formData.append('vid', String(params.vid))

  return formData
}

export function toAtAtRequest(atAtParams: AtAtRequestBody, origin?: string) {
  const body = createAtAtFormData(atAtParams)
  const token = getAtAtToken()

  // Start with an Option of `{ origin }` or an empty object
  // Then optionally add the token
  const headers = {
    ...(origin && { origin }),
    ...(token && { authorization: `Bearer ${token}` })
  }

  return {
    method: 'POST',
    body,
    headers
  }
}

export function atatFetch(
  atatRequestBody: AtAtRequestBody,
  handleFailure: ErrorHandler,
  handleSuccess: SuccessHandler
) {
  const atatRequest = toAtAtRequest(atatRequestBody)

  const atatFetchWithRetry = (retryCount: number): T.Task<void> =>
    pipe(
      safeFetchJson<Record<string, string>>(atatUrl, atatRequest),
      TE.fold(
        e =>
          retryCount > 0
            ? atatFetchWithRetry(retryCount - 1)
            : pipe(
                responseToError(e),
                E.fold(
                  error => T.of(handleFailure(error)),
                  error => T.of(handleFailure(error))
                )
              ),
        r => T.of(handleSuccess(r))
      )
    )

  return atatFetchWithRetry(MAX_ATAT_RETRY_COUNT)()
}
